"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  Check,
  ChevronRight,
  Loader2,
  PlusCircle,
  TriangleAlert,
} from "lucide-react";

import type { RouterOutputs } from "@acme/api";
import type { TransientRepositorySchemaType } from "@acme/validators";
import { cn } from "@acme/ui";
import { Avatar, AvatarFallback, AvatarImage } from "@acme/ui/avatar";
import { Button } from "@acme/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@acme/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@acme/ui/popover";
import { ScrollArea } from "@acme/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@acme/ui/tooltip";
import { TruncatedTooltip } from "@acme/ui/truncated-tooltip";

import { useSwitchTeam } from "~/hooks/use-switch-team";
import { useTRPC } from "~/trpc/react";
import { redirectToGithubInstallation } from "~/utils/github-installation";

type InstallationRepoGroup =
  RouterOutputs["github"]["listInstallationsAndRepos"][number];

interface RepositoryDropdownProps {
  value?: TransientRepositorySchemaType[];
  onChange: (value: TransientRepositorySchemaType[]) => void;
  className?: string;
  includeInactiveOrganizationsRepositories?: boolean;
}

export const RepositoryDropdown = forwardRef<
  HTMLButtonElement,
  RepositoryDropdownProps
>(function RepositoryDropdown(
  {
    value,
    onChange,
    className,
    includeInactiveOrganizationsRepositories = false,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [isRedirectingToInstallation, startTransition] = useTransition();
  const trpc = useTRPC();

  const { handleSetActiveOrganization } = useSwitchTeam();

  const {
    data: installationRepoGroups,
    refetch: refetchInstallationRepoGroups,
  } = useSuspenseQuery(
    trpc.github.listInstallationsAndRepos.queryOptions(
      {
        includeInactiveOrganizationsRepositories,
      },
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      },
    ),
  );

  const sortedInstallationRepoGroups = useMemo(() => {
    return installationRepoGroups.sort((a, b) => {
      return a.isActiveOrganization && !b.isActiveOrganization ? -1 : 0;
    });
  }, [installationRepoGroups]);

  const [selectedTeam, setSelectedTeam] = useState(
    sortedInstallationRepoGroups[0]?.gitRepoOwner,
  );

  const [currentHoveredTeam, setCurrentHoveredTeam] = useState(
    selectedTeam?.id,
  );

  const filteredRepos = useMemo(() => {
    return installationRepoGroups.find(
      (repo) => repo.gitRepoOwner.id === currentHoveredTeam,
    );
  }, [currentHoveredTeam, installationRepoGroups]);

  const selectedRepoNames = useMemo(() => {
    const result: string[] = [];
    // TODO: Really poor performance, but it works for now
    for (const group of installationRepoGroups) {
      for (const repo of group.repositories) {
        if (value?.some((r) => r.repositoryId === repo.id.toString())) {
          result.push(repo.name);
        }
      }
    }
    if (result.length === 0) {
      return "Select Repo";
    }
    return result.join(", ");
  }, [value, installationRepoGroups]);

  const selectRepo = useCallback(
    (
      group: InstallationRepoGroup,
      repo: InstallationRepoGroup["repositories"][number],
    ) => {
      const changeRepo = () => {
        onChange([
          {
            gitAccount: {
              id: Number(group.gitAccountId),
              provider: group.gitRepoOwner.provider,
            },
            repositoryId: repo.id.toString(),
          },
        ]);
      };

      if (!group.isActiveOrganization) {
        setSelectedTeam(group.gitRepoOwner);
        setCurrentHoveredTeam(group.gitRepoOwner.id);
        void handleSetActiveOrganization(group.organizationId ?? null).then(
          () => {
            void refetchInstallationRepoGroups();

            changeRepo();
          },
        );
      } else {
        changeRepo();
      }
    },
    [handleSetActiveOrganization, refetchInstallationRepoGroups, onChange],
  );

  const router = useRouter();
  const currentPathname = usePathname();
  const redirectToInstallation = useCallback(() => {
    startTransition(() => {
      redirectToGithubInstallation(router, currentPathname);
    });
  }, [router, currentPathname]);

  useEffect(() => {
    if (value?.[0]) {
      const group = installationRepoGroups.find((group) =>
        group.repositories.some(
          (repo) => repo.id.toString() === value[0]?.repositoryId,
        ),
      );
      if (group) {
        setSelectedTeam(group.gitRepoOwner);
        setCurrentHoveredTeam(group.gitRepoOwner.id);
      }
    }
  }, [installationRepoGroups, value]);

  const heightClassName = "h-[300px]";

  const accountName = selectedTeam?.name ?? selectedTeam?.login;

  const isSelectedTeamActiveOrganization = useMemo(() => {
    return (
      includeInactiveOrganizationsRepositories ||
      installationRepoGroups.find(
        (group) => group.gitRepoOwner.id === currentHoveredTeam,
      )?.isActiveOrganization
    );
  }, [
    currentHoveredTeam,
    includeInactiveOrganizationsRepositories,
    installationRepoGroups,
  ]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <TooltipProvider>
        <PopoverTrigger asChild ref={ref}>
          <Button
            variant="outline"
            className={cn(
              "flex h-auto w-full min-w-0 items-center gap-1 pr-1 pl-2 text-sm font-bold transition-all",
              className,
            )}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="shrink-0">
                  <Avatar className="size-5">
                    <AvatarImage
                      src={selectedTeam?.avatarUrl ?? undefined}
                      alt={accountName}
                    />
                    <AvatarFallback>
                      {accountName?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </TooltipTrigger>
              <TooltipContent className="truncate">
                {accountName}
              </TooltipContent>
            </Tooltip>

            <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
              <ChevronRight className="text-muted-foreground size-3 shrink-0" />

              <TruncatedTooltip
                tooltipClassName="truncate font-mono font-bold"
                className="text-foreground max-w-full truncate font-mono"
              >
                {selectedRepoNames}
              </TruncatedTooltip>
            </div>
          </Button>
        </PopoverTrigger>
      </TooltipProvider>

      <PopoverContent
        className="bg-background/60 w-[540px] overflow-hidden p-0 shadow-2xl xl:w-[640px]"
        align="start"
        sideOffset={8}
      >
        {sortedInstallationRepoGroups.length === 0 && (
          <div className="bg-background flex h-full flex-col items-center justify-center gap-4 p-4">
            <div className="text-muted-foreground text-sm">
              <div className="flex w-80 flex-col items-center justify-center gap-2 text-center">
                <GitHubLogoIcon className="size-12" />
                <p>
                  You don't have any repositories connected. Please connect your
                  repositories to get started.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={redirectToInstallation}
              loading={isRedirectingToInstallation}
            >
              Connect repositories
            </Button>
          </div>
        )}
        {sortedInstallationRepoGroups.length > 0 && (
          <div className="grid grid-cols-3">
            {/* Team Selection */}
            <div className="border-border bg-background col-span-1 border-r">
              <Command className="flex h-full flex-col rounded-none bg-transparent">
                <CommandInput
                  className="text-sm"
                  placeholder="Search teams..."
                />
                <CommandList className="flex-1 overflow-hidden">
                  <ScrollArea className="h-full">
                    <CommandEmpty>No teams found.</CommandEmpty>
                    {sortedInstallationRepoGroups.map((team) => (
                      <CommandItem
                        key={team.gitRepoOwner.id}
                        className="cursor-pointer"
                        onMouseEnter={() => {
                          setCurrentHoveredTeam(team.gitRepoOwner.id);
                        }}
                        onSelect={() => {
                          setSelectedTeam(team.gitRepoOwner);
                          setCurrentHoveredTeam(team.gitRepoOwner.id);
                          onChange([]);
                        }}
                      >
                        <Avatar className="mr-2 size-5">
                          <AvatarImage
                            src={team.gitRepoOwner.avatarUrl ?? undefined}
                            alt={team.gitRepoOwner.login}
                          />
                          <AvatarFallback>
                            {team.gitRepoOwner.name?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        {team.gitRepoOwner.login}
                        <Check
                          className={cn(
                            "ml-auto size-4",
                            selectedTeam?.id === team.gitRepoOwner.id
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                  </ScrollArea>
                </CommandList>
                <CommandSeparator />
                <CommandItem
                  className="cursor-pointer gap-2 rounded-none px-3 py-3"
                  onSelect={redirectToInstallation}
                  disabled={isRedirectingToInstallation}
                >
                  {isRedirectingToInstallation ? (
                    <Loader2 className="size-5 animate-spin" />
                  ) : (
                    <PlusCircle className="size-5" />
                  )}
                  <span className="font-bold">Connect repositories</span>
                </CommandItem>
              </Command>
            </div>

            {/* Repository Selection */}
            <div className="bg-background col-span-2">
              <Command className="flex h-full flex-col rounded-none bg-transparent">
                <CommandInput
                  className="text-sm"
                  placeholder="Search repositories..."
                />
                <CommandList className="max-h-full overflow-hidden">
                  <ScrollArea className={heightClassName}>
                    {filteredRepos?.repositories.length ? (
                      <CommandGroup heading="Repositories">
                        {filteredRepos.repositories.map((repo) => (
                          <CommandItem
                            key={repo.id}
                            className="grid cursor-pointer grid-cols-[1fr_20px] items-center font-mono"
                            onSelect={() => {
                              selectRepo(filteredRepos, repo);
                              setOpen(false);
                            }}
                          >
                            <div className="truncate">{repo.name}</div>
                            <Check
                              className={cn(
                                "size-4 justify-self-end",
                                value?.some(
                                  (r) => r.repositoryId === repo.id.toString(),
                                )
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    ) : (
                      <div className="flex h-full min-h-[280px] items-center justify-center">
                        <CommandEmpty>
                          {isSelectedTeamActiveOrganization ? (
                            <div className="flex flex-col items-center justify-center gap-3 px-6 py-8 text-center">
                              <div className="bg-muted/50 rounded-full p-3">
                                <GitHubLogoIcon className="text-muted-foreground size-6" />
                              </div>
                              <div className="space-y-1">
                                <p className="text-foreground text-sm font-medium">
                                  No repositories found
                                </p>
                                <p className="text-muted-foreground text-xs">
                                  Try adjusting your search or check if
                                  repositories exist
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center gap-4 px-2 py-8 text-center">
                              <div className="rounded-full bg-amber-500/10 p-3">
                                <TriangleAlert className="size-6 text-amber-500" />
                              </div>
                              <div className="space-y-2">
                                <p className="text-foreground text-base font-medium">
                                  Access Required
                                </p>
                                <div className="text-muted-foreground space-y-2 text-sm">
                                  <p className="text-balance">
                                    Your current organization doesn't have
                                    access to these repositories.
                                  </p>
                                  <p className="text-balance">
                                    To access them, please select the relevant
                                    team from the left panel.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </CommandEmpty>
                      </div>
                    )}
                  </ScrollArea>
                </CommandList>
              </Command>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});
